/**
 * Image Storage Utility
 * Ruajtja e fotove në memorien globale + IndexedDB për persistim pas refresh
 */

let storedImages = [];
let orderMetadata = null;

// --- IndexedDB helpers ---

const DB_NAME = 'fletaime-orders';
const DB_VERSION = 1;

const openDB = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = ({ target: { result: db } }) => {
      if (!db.objectStoreNames.contains('images')) {
        db.createObjectStore('images', { keyPath: 'idx' });
      }
      if (!db.objectStoreNames.contains('metadata')) {
        db.createObjectStore('metadata', { keyPath: 'id' });
      }
    };
  });

const persistToIDB = async (images, metadata) => {
  try {
    const db = await openDB();
    const tx = db.transaction(['images', 'metadata'], 'readwrite');
    const imgStore = tx.objectStore('images');
    const metaStore = tx.objectStore('metadata');
    imgStore.clear();
    metaStore.clear();
    images.forEach((img, idx) => {
      if (img.file) imgStore.put({ idx, file: img.file, name: img.name || img.file.name });
    });
    // Ruaj metadata pa uploadedImages (ato ruhen veçmas si File objekte)
    const { uploadedImages: _drop, ...metaOnly } = metadata || {};
    metaStore.put({ id: 'current', ...metaOnly });
    await new Promise((res, rej) => {
      tx.oncomplete = res;
      tx.onerror = () => rej(tx.error);
    });
  } catch (e) {
    console.warn('IndexedDB save failed:', e);
  }
};

const clearIDB = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(['images', 'metadata'], 'readwrite');
    tx.objectStore('images').clear();
    tx.objectStore('metadata').clear();
    await new Promise((res) => { tx.oncomplete = res; tx.onerror = res; });
  } catch (e) {
    console.warn('IndexedDB clear failed:', e);
  }
};

// --- Public API ---

export const storeImagesForOrder = (images, metadata) => {
  clearStoredImages();
  storedImages = images || [];
  orderMetadata = metadata || null;
  // Ruaj edhe në IndexedDB për restaurim pas refresh
  persistToIDB(storedImages, { ...orderMetadata });
  console.log(`Stored ${storedImages.length} images for order`);
  return true;
};

export const getStoredImages = () => storedImages;

export const getOrderMetadata = () => orderMetadata;

export const hasStoredImages = () =>
  storedImages.length > 0 || orderMetadata !== null;

export const clearStoredImages = () => {
  storedImages.forEach(img => {
    if (img.url && img.url.startsWith('blob:')) {
      try { URL.revokeObjectURL(img.url); } catch (e) {}
    }
  });
  storedImages = [];
  orderMetadata = null;
  clearIDB();
  console.log('Cleared stored images');
};

export const getFullOrderData = () => {
  if (!orderMetadata) return null;
  return { ...orderMetadata, uploadedImages: storedImages };
};

/**
 * Restauro të dhënat e porosisë nga IndexedDB pas refresh.
 * Kthehet { images, metadata } ose null nëse nuk ka të dhëna.
 */
export const restoreStoredImages = async () => {
  // Nëse ka të dhëna në memorie, nuk duhet IndexedDB
  if (storedImages.length > 0 || orderMetadata) return null;

  try {
    const db = await openDB();
    const tx = db.transaction(['images', 'metadata'], 'readonly');
    const imgReq = tx.objectStore('images').getAll();
    const metaReq = tx.objectStore('metadata').get('current');
    await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });

    const records = (imgReq.result || []).sort((a, b) => a.idx - b.idx);
    const meta = metaReq.result;

    if (!meta || records.length === 0) return null;

    // Rikrijojmë blob URLs nga File objektet e ruajtura
    const images = records.map(r => ({
      url: URL.createObjectURL(r.file),
      file: r.file,
      name: r.name,
    }));

    // Rikthe edhe në memorie për shfrytëzim të mëtejshëm
    storedImages = images;
    orderMetadata = meta;

    return { images, metadata: meta };
  } catch (e) {
    console.warn('IndexedDB restore failed:', e);
    return null;
  }
};

// --- PhotoBook in-memory Storage ---

let photoBookData = null;

export const storePhotoBookData = (data) => {
  photoBookData = data;
  console.log('Stored PhotoBook data');
  return true;
};

export const getPhotoBookData = () => photoBookData;

export const clearPhotoBookData = () => {
  photoBookData = null;
  console.log('Cleared PhotoBook data');
};

// =============================================
// PhotoBook IndexedDB – persiston pas refresh
// =============================================

const PB_DB_NAME = 'fletaime-photobook';
const PB_DB_VERSION = 1;

const openPBDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(PB_DB_NAME, PB_DB_VERSION);
  req.onerror = () => reject(req.error);
  req.onsuccess = () => resolve(req.result);
  req.onupgradeneeded = ({ target: { result: db } }) => {
    if (!db.objectStoreNames.contains('photos')) {
      db.createObjectStore('photos', { keyPath: 'id' });
    }
    if (!db.objectStoreNames.contains('session')) {
      db.createObjectStore('session', { keyPath: 'key' });
    }
  };
});

/**
 * Ruaj PhotoBook-un (foto + faqe + productData) në IndexedDB.
 * Blob URLs nuk ruhen – rindërtohen nga File objektet gjatë restaurimit.
 */
export const savePhotoBookToIDB = async ({ uploadedPhotos, pages, productData, coverData }) => {
  try {
    const db = await openPBDB();
    const tx = db.transaction(['photos', 'session'], 'readwrite');
    const photoStore = tx.objectStore('photos');
    const sessionStore = tx.objectStore('session');

    photoStore.clear();

    for (const photo of uploadedPhotos) {
      if (photo.file) {
        photoStore.put({ id: photo.id, file: photo.file, name: photo.name });
      }
    }

    // Serializo faqet pa blob URLs (vetëm metadata + photo id për lookup)
    const serializedPages = pages.map(page => ({
      id: page.id,
      layout: page.layout,
      backgroundColor: page.backgroundColor,
      textElements: page.textElements || [],
      slotSizes: page.slotSizes || {},
      images: Object.fromEntries(
        Object.entries(page.images || {})
          .filter(([, img]) => img != null)
          .map(([slot, img]) => [
            slot,
            {
              id: img.id,
              name: img.name,
              zoom: img.zoom,
              panX: img.panX,
              panY: img.panY,
              panSlotW: img.panSlotW,
              panSlotH: img.panSlotH,
            }
          ])
      )
    }));

    // Ruaj coverData vetëm me style/meta/layout (pa imazhe base64 në slots).
    // Stickers ruhen të plota (janë vetëm URL + numra, jo blobe) - nuk kanë
    // arsye madhësie/memorie për t'u fshirë si sloteve e fotove.
    const coverDataToSave = coverData ? {
      activeSide: coverData.activeSide,
      style: coverData.style,
      meta: coverData.meta,
      frontLayout: coverData.frontLayout,
      backLayout: coverData.backLayout,
      frontStickers: coverData.frontStickers || [],
      backStickers: coverData.backStickers || [],
      spineSticker: coverData.spineSticker || null,
    } : null;

    sessionStore.put({ key: 'pages', value: serializedPages });
    sessionStore.put({ key: 'productData', value: productData });
    sessionStore.put({ key: 'coverData', value: coverDataToSave });

    await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });
  } catch (e) {
    console.warn('PhotoBook IDB save failed:', e);
  }
};

/**
 * Restauro PhotoBook-un nga IndexedDB pas refresh.
 * Rindërton blob URLs nga File objektet e ruajtura.
 */
export const loadPhotoBookFromIDB = async () => {
  try {
    const db = await openPBDB();
    const tx = db.transaction(['photos', 'session'], 'readonly');
    const photosReq  = tx.objectStore('photos').getAll();
    const pagesReq   = tx.objectStore('session').get('pages');
    const productReq = tx.objectStore('session').get('productData');
    const coverReq   = tx.objectStore('session').get('coverData');

    await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });

    const photoRecords  = photosReq.result  || [];
    const pagesRecord   = pagesReq.result;
    const productRecord = productReq.result;

    if (!pagesRecord || !productRecord || photoRecords.length === 0) return null;

    // Rindërtojmë blob URLs dhe ndërtojmë photo map (id → src)
    const photoMap = {};
    const uploadedPhotos = photoRecords.map(r => {
      const src = URL.createObjectURL(r.file);
      photoMap[r.id] = src;
      return { id: r.id, src, file: r.file, name: r.name };
    });

    // Rindërtojmë faqet duke zëvendësuar photo id me blob URL
    const pages = pagesRecord.value.map(page => ({
      ...page,
      images: Object.fromEntries(
        Object.entries(page.images || {})
          .map(([slot, img]) => {
            const src = photoMap[img.id];
            return src ? [slot, { ...img, src }] : null;
          })
          .filter(Boolean)
      )
    }));

    return {
      uploadedPhotos,
      pages,
      productData: productRecord.value,
      coverData: coverReq.result?.value || null,
    };
  } catch (e) {
    console.warn('PhotoBook IDB load failed:', e);
    return null;
  }
};

/**
 * Pastro PhotoBook IndexedDB (pas dërgimit të porosisë).
 */
export const clearPhotoBookIDB = async () => {
  try {
    const db = await openPBDB();
    const tx = db.transaction(['photos', 'session'], 'readwrite');
    tx.objectStore('photos').clear();
    tx.objectStore('session').clear();
    await new Promise((res) => { tx.oncomplete = res; tx.onerror = res; });
    console.log('PhotoBook IDB cleared');
  } catch (e) {
    console.warn('PhotoBook IDB clear failed:', e);
  }
};

